import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarCheck, Clock, Filter, Users } from 'lucide-react'
import { format } from 'date-fns'
import { attendanceApi } from '../api/attendance'
import { Avatar } from '../components/common/Avatar'
import { Badge } from '../components/common/Badge'
import { Button } from '../components/common/Button'
import { useAuthStore } from '../store/authStore'
import { hasFullAccess } from '../lib/roles'
import type { AttendanceRecord, AttendanceShift, AttendanceStatus } from '../types'

const todayDateInput = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const statusColors: Record<AttendanceStatus, string> = {
  ON_TIME: '#16a34a',
  LATE: '#dc2626',
  LATE_WITH_PERMISSION: '#d97706',
}

function statusLabel(status?: AttendanceStatus | null) {
  if (!status) return 'Pending'
  if (status === 'ON_TIME') return 'On Time'
  if (status === 'LATE_WITH_PERMISSION') return 'Late with Permission'
  return 'Late'
}

function formatTime(value?: string | null) {
  if (!value) return '-'
  return format(new Date(value), 'h:mm a')
}

function formatDate(value: string) {
  return format(new Date(value), 'dd MMM yyyy')
}

function formatMinutes(value?: number | null) {
  if (!value || value <= 0) return '0h'
  const hours = Math.floor(value / 60)
  const minutes = value % 60
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
}

function reasonText(record: AttendanceRecord) {
  if (record.lateReason) return record.lateReason
  if (record.lateType === 'ACKNOWLEDGED') return 'Acknowledged'
  return '-'
}

function formatBreaks(record: AttendanceRecord) {
  if (record.breaks.length === 0) return '-'
  return record.breaks.map((attendanceBreak) => `${formatTime(attendanceBreak.startedAt)} – ${formatTime(attendanceBreak.endedAt)}`).join(', ')
}

function apiError(error: unknown): string {
  return (error as { response?: { data?: { error?: string } } })?.response?.data?.error
    || (error as Error)?.message
    || 'Something went wrong.'
}

export default function AttendancePage() {
  const { user } = useAuthStore()
  const canManageAttendance = hasFullAccess(user?.role)
  const qc = useQueryClient()
  const today = todayDateInput()
  const [dateMode, setDateMode] = useState<'TODAY' | 'CUSTOM'>('TODAY')
  const [employeeId, setEmployeeId] = useState('all')
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)
  const [status, setStatus] = useState<'ALL' | AttendanceStatus>('ALL')
  const [lateMode, setLateMode] = useState<'ALL' | 'LATE_ONLY' | 'LATE_WITH_PERMISSION'>('ALL')
  const [slackChannelId, setSlackChannelId] = useState('')
  const [settingsMessage, setSettingsMessage] = useState('')

  const recordsQuery = useQuery({
    queryKey: ['attendance', 'records', employeeId, startDate, endDate, status, lateMode],
    queryFn: () => attendanceApi.listRecords({
      employeeId: employeeId === 'all' ? undefined : employeeId,
      startDate,
      endDate,
      status: status === 'ALL' ? undefined : status,
      lateOnly: lateMode === 'LATE_ONLY',
      lateWithPermissionOnly: lateMode === 'LATE_WITH_PERMISSION',
    }).then((res) => res.data.data),
  })

  const shiftsQuery = useQuery({
    queryKey: ['attendance', 'shifts'],
    queryFn: () => attendanceApi.listShifts().then((res) => res.data.data),
    enabled: canManageAttendance,
  })

  const settingsQuery = useQuery({
    queryKey: ['attendance', 'settings'],
    queryFn: () => attendanceApi.getSettings().then((res) => res.data.data),
    enabled: canManageAttendance,
  })

  useEffect(() => {
    if (settingsQuery.data) setSlackChannelId(settingsQuery.data.slackChannelId || '')
  }, [settingsQuery.data])

  const settingsMutation = useMutation({
    mutationFn: () => attendanceApi.updateSettings({ slackChannelId: slackChannelId.trim() }),
    onSuccess: () => {
      setSettingsMessage('Saved')
      qc.invalidateQueries({ queryKey: ['attendance', 'settings'] })
    },
    onError: (error) => setSettingsMessage(apiError(error)),
  })

  const records = recordsQuery.data?.records || []
  const employees = recordsQuery.data?.employees || []
  const lateCount = records.filter((record) => record.status === 'LATE').length
  const permissionCount = records.filter((record) => record.status === 'LATE_WITH_PERMISSION').length
  const onTimeCount = records.filter((record) => record.status === 'ON_TIME').length

  return (
    <div style={pageStyle}>
      <div style={heroStyle}>
        <div>
          <div style={eyebrowStyle}>Employee attendance</div>
          <h1 style={titleStyle}>Attendance</h1>
          <p style={subtitleStyle}>
            Daily start, break, finish, and late-start records.
          </p>
        </div>
        <div style={summaryGridStyle}>
          <Summary icon={<CalendarCheck size={17} />} label="On Time" value={String(onTimeCount)} color="#16a34a" />
          <Summary icon={<Clock size={17} />} label="Late" value={String(lateCount)} color="#dc2626" />
          <Summary icon={<Users size={17} />} label="Permission" value={String(permissionCount)} color="#d97706" />
        </div>
      </div>

      <div style={filterBarStyle}>
        {canManageAttendance && (
          <label style={labelStyle}>
            Employee
            <select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)} style={inputStyle}>
              <option value="all">All employees</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>{employee.fullName}</option>
              ))}
            </select>
          </label>
        )}
        <label style={labelStyle}>
          View
          <select
            value={dateMode}
            onChange={(event) => {
              const next = event.target.value as 'TODAY' | 'CUSTOM'
              setDateMode(next)
              if (next === 'TODAY') {
                setStartDate(today)
                setEndDate(today)
              }
            }}
            style={inputStyle}
          >
            <option value="TODAY">Today</option>
            <option value="CUSTOM">Custom</option>
          </select>
        </label>
        {dateMode === 'CUSTOM' && (
          <>
            <label style={labelStyle}>
              Start
              <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              End
              <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} style={inputStyle} />
            </label>
          </>
        )}
        <label style={labelStyle}>
          Status
          <select value={status} onChange={(event) => setStatus(event.target.value as 'ALL' | AttendanceStatus)} style={inputStyle}>
            <option value="ALL">All status</option>
            <option value="ON_TIME">On Time</option>
            <option value="LATE">Late</option>
            <option value="LATE_WITH_PERMISSION">Late with Permission</option>
          </select>
        </label>
        <label style={labelStyle}>
          Late filter
          <select value={lateMode} onChange={(event) => setLateMode(event.target.value as typeof lateMode)} style={inputStyle}>
            <option value="ALL">All records</option>
            <option value="LATE_ONLY">Late only</option>
            <option value="LATE_WITH_PERMISSION">Late with permission only</option>
          </select>
        </label>
        <button type="button" onClick={() => { setDateMode('TODAY'); setStartDate(today); setEndDate(today) }} style={todayButtonStyle}>
          <Filter size={14} /> Today
        </button>
      </div>

      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {canManageAttendance && <th style={thStyle}>Employee name</th>}
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Start time</th>
              <th style={thStyle}>Finish time</th>
              <th style={thStyle}>Breaks</th>
              <th style={thStyle}>Total worked hours</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Late reason / permission reason</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={record.id} style={record.status === 'LATE' ? lateRowStyle : record.status === 'LATE_WITH_PERMISSION' ? permissionRowStyle : undefined}>
                {canManageAttendance && (
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar user={record.user} size="sm" />
                      <span>{record.user.fullName}</span>
                    </div>
                  </td>
                )}
                <td style={tdStyle}>{formatDate(record.attendanceDate)}</td>
                <td style={tdStyle}>{formatTime(record.startedAt)}</td>
                <td style={tdStyle}>{formatTime(record.finishedAt)}</td>
                <td style={{ ...tdStyle, minWidth: 180 }}>{formatBreaks(record)}</td>
                <td style={tdStyle}>{formatMinutes(record.totalWorkedMinutes)}</td>
                <td style={tdStyle}><AttendanceBadge status={record.status} /></td>
                <td style={tdStyle}>
                  <div>{reasonText(record)}</div>
                  {record.workLogShortfallReason && (
                    <div style={{ marginTop: 4, color: '#b45309', fontSize: 12 }}>
                      Work log: {formatMinutes(record.workLogMinutes)} logged. {record.workLogShortfallReason}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!recordsQuery.isLoading && records.length === 0 && (
              <tr>
                <td style={emptyCellStyle} colSpan={canManageAttendance ? 8 : 7}>No attendance records found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canManageAttendance && (
        <div style={sectionStyle}>
          <div style={{ marginBottom: 18 }}>
            <h2 style={sectionTitleStyle}>Slack Feed</h2>
            <p style={sectionSubtitleStyle}>Attendance start, break, and finish messages will be posted to this Slack channel.</p>
            <div style={slackSettingStyle}>
              <label style={{ ...labelStyle, flex: 1 }}>
                Attendance Slack Channel ID
                <input
                  value={slackChannelId}
                  onChange={(event) => {
                    setSlackChannelId(event.target.value)
                    setSettingsMessage('')
                  }}
                  placeholder="e.g. C0123456789"
                  style={{ ...inputStyle, width: '100%' }}
                />
              </label>
              <Button loading={settingsMutation.isPending} onClick={() => settingsMutation.mutate()}>
                Save
              </Button>
              {settingsMessage && (
                <span style={{ color: settingsMessage === 'Saved' ? '#16a34a' : '#dc2626', fontSize: 13, fontWeight: 700 }}>
                  {settingsMessage}
                </span>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <h2 style={sectionTitleStyle}>Shift Setup</h2>
            <p style={sectionSubtitleStyle}>Managers, admins, and super admins can set each employee's local shift start time.</p>
          </div>
          <div style={shiftGridStyle}>
            {(shiftsQuery.data || []).map((shift) => (
              <ShiftRow
                key={shift.user.id}
                shift={shift}
                onSaved={() => {
                  qc.invalidateQueries({ queryKey: ['attendance'] })
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function AttendanceBadge({ status }: { status?: AttendanceStatus | null }) {
  if (!status) return <Badge color="#64748b" label="Pending" />
  return <Badge color={statusColors[status]} label={statusLabel(status)} />
}

function Summary({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <div style={summaryStyle}>
      <div style={{ ...summaryIconStyle, color, background: `${color}14` }}>{icon}</div>
      <div>
        <div style={summaryLabelStyle}>{label}</div>
        <strong style={summaryValueStyle}>{value}</strong>
      </div>
    </div>
  )
}

function ShiftRow({ shift, onSaved }: { shift: AttendanceShift; onSaved: () => void }) {
  const [shiftStartTime, setShiftStartTime] = useState(shift.shiftStartTime)
  const [timeZone, setTimeZone] = useState(shift.timeZone)
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: () => attendanceApi.updateShift(shift.user.id, { shiftStartTime, timeZone }),
    onSuccess: () => {
      setError('')
      onSaved()
    },
    onError: (err) => setError(apiError(err)),
  })

  return (
    <div style={shiftRowStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <Avatar user={shift.user} size="sm" />
        <div style={{ minWidth: 0 }}>
          <strong style={{ display: 'block', color: '#0f172a', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {shift.user.fullName}
          </strong>
          <span style={{ color: '#64748b', fontSize: 12 }}>{shift.user.email}</span>
        </div>
      </div>
      <input type="time" value={shiftStartTime} onChange={(event) => setShiftStartTime(event.target.value)} style={shiftInputStyle} />
      <input value={timeZone} onChange={(event) => setTimeZone(event.target.value)} style={shiftInputStyle} placeholder="Asia/Kolkata" />
      <Button size="sm" loading={mutation.isPending} onClick={() => mutation.mutate()}>Save</Button>
      {error && <div style={{ color: '#dc2626', fontSize: 12, gridColumn: '1 / -1' }}>{error}</div>}
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
}

const heroStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 18,
}

const eyebrowStyle: React.CSSProperties = {
  color: '#2563eb',
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
}

const titleStyle: React.CSSProperties = {
  margin: '4px 0',
  fontSize: 30,
  color: '#0f172a',
  letterSpacing: 0,
}

const subtitleStyle: React.CSSProperties = {
  margin: 0,
  color: '#64748b',
  fontSize: 14,
}

const summaryGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(130px, 1fr))',
  gap: 10,
}

const summaryStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '10px 12px',
}

const summaryIconStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const summaryLabelStyle: React.CSSProperties = {
  color: '#64748b',
  fontSize: 12,
  fontWeight: 700,
}

const summaryValueStyle: React.CSSProperties = {
  color: '#0f172a',
  fontSize: 18,
}

const filterBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'end',
  gap: 10,
  flexWrap: 'wrap',
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: 12,
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  color: '#475569',
  fontSize: 12,
  fontWeight: 700,
}

const inputStyle: React.CSSProperties = {
  height: 34,
  minWidth: 150,
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '0 9px',
  color: '#0f172a',
  background: '#ffffff',
}

const todayButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 34,
  padding: '0 12px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#f8fafc',
  color: '#0f172a',
  cursor: 'pointer',
  fontWeight: 700,
}

const tableWrapStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  overflowX: 'auto',
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: 980,
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 14px',
  color: '#64748b',
  fontSize: 12,
  textTransform: 'uppercase',
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  color: '#0f172a',
  fontSize: 13,
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'middle',
}

const emptyCellStyle: React.CSSProperties = {
  padding: 30,
  textAlign: 'center',
  color: '#64748b',
  fontSize: 14,
}

const lateRowStyle: React.CSSProperties = {
  background: '#fef2f2',
}

const permissionRowStyle: React.CSSProperties = {
  background: '#fffbeb',
}

const sectionStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: 16,
}

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  color: '#0f172a',
  fontSize: 18,
}

const sectionSubtitleStyle: React.CSSProperties = {
  margin: '4px 0 0',
  color: '#64748b',
  fontSize: 13,
}

const shiftGridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 10,
}

const slackSettingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'end',
  gap: 10,
  marginTop: 12,
  flexWrap: 'wrap',
  maxWidth: 680,
}

const shiftRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(220px, 1fr) 120px minmax(180px, 240px) auto',
  alignItems: 'center',
  gap: 10,
  padding: 10,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
}

const shiftInputStyle: React.CSSProperties = {
  height: 32,
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '0 9px',
}
